import { useQuery, UseQueryResult } from "react-query";

import { getDocumentChecklistStatus } from "api/documentGenerationChecklist";

export interface DocumentOverride {
  /** Name of the supervisor who granted it; null when they have no name on record */
  grantedByName: string | null;
}

export interface DocumentChecklistStatus {
  /** Document type key (e.g. "initial_treatment_plan") */
  documentType: string;
  /** True iff any checklist item is not present */
  missingChecklistItems: boolean;
  /** True iff any assessment categorizes as "missing" */
  missingAssessments: boolean;
  /** Set on the one entry the client's active override applies to, null on the rest */
  override: DocumentOverride | null;
}

export interface ActiveDocumentOverride extends DocumentOverride {
  documentType: string;
}

interface UseGetDocumentChecklistStatusReturn {
  statuses: DocumentChecklistStatus[];
  /** At most one override is active per client, so this collapses the six entries to one. */
  activeOverride: ActiveDocumentOverride | null;
  statusesLoading: boolean;
  refetchStatuses: UseQueryResult<{ data: DocumentChecklistStatus[] }>["refetch"];
}

/**
 * Fetches missing item counts for all document types for a given client.
 * @param clientId - The client ID.
 * @param clinicianId - Optional. When provided, enables the IR clinician credentials check.
 * @param options.enabled - When false, pauses the query without changing the queryKey (so cached data persists for callers that briefly disable the fetch — e.g. dialog close animations).
 */
const useGetDocumentChecklistStatus = (
  clientId?: string | number,
  clinicianId?: string | number,
  options?: { enabled?: boolean }
): UseGetDocumentChecklistStatusReturn &
  Omit<UseQueryResult, "data" | "isLoading" | "refetch"> => {
  const { data, isLoading, refetch, ...rest } = useQuery(
    ["documentChecklistStatus", clientId, clinicianId],
    () => getDocumentChecklistStatus(clientId!, clinicianId),
    {
      enabled: Boolean(clientId) && (options?.enabled ?? true),
      staleTime: 0,
      retry: false,
    }
  );

  const statuses: DocumentChecklistStatus[] = data?.data || [];
  const overridden = statuses.find((status) => status.override);

  return {
    statuses,
    activeOverride: overridden
      ? { documentType: overridden.documentType, ...overridden.override! }
      : null,
    statusesLoading: isLoading,
    refetchStatuses: refetch,
    ...rest,
  };
};

export default useGetDocumentChecklistStatus;
